// React and Hooks
import { memo, useEffect, useState } from "react";

// Types and Props
import { TehnicalInspectionEditProps } from "./types";
import { TehnicalInspectionEditFormValues } from "services/api/calculations/tehnicalinspection/types";
import { VehicleGroupRow } from "services/api/calculations/vehiclegroup/types";

// API Calls
import {
  createTehnicalInspectionAPI,
  getTehnicalInspectionAPI,
  updateTehnicalInspectionAPI,
} from "services/api/calculations/tehnicalinspection/api";
import { getVehicleGroupsAPI } from "services/api/calculations/vehiclegroup/api";

// Constants
import { initialTehnicalInspectionEditForm } from "services/api/calculations/tehnicalinspection/consts";
import { initialDataWithPagination } from "services/api/consts";
import {
  DataWithPagination,
  initialPagination,
} from "ui/components/Table/consts";

// Validation
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { CALCULATION_TEHNICAL_INSPECTION_EDIT_FORM_SCHEMA } from "./validation";

// Hooks
import {
  useHandleDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";

// UI Components
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

// Helpers
import { useTranslate } from "helpers";

const TehnicalInspectionEditCard: React.FC<TehnicalInspectionEditProps> = (
  props
) => {
  const { t } = useTranslate();
  const { tehnicalInspectionId, onSuccess } = props;
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [vehicleGroups, setVehicleGroups] = useState<
    DataWithPagination<VehicleGroupRow>
  >(initialDataWithPagination);

  const [formValues, setFormValues] =
    useState<TehnicalInspectionEditFormValues>(
      initialTehnicalInspectionEditForm
    );

  useEffect(() => {
    if (tehnicalInspectionId) {
      fetchTehnicalInspection(tehnicalInspectionId);
    }
    fetchVehicleGroups();
  }, [tehnicalInspectionId]);

  const fetchTehnicalInspection = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialTehnicalInspectionEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getTehnicalInspectionAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const fetchVehicleGroups = async () => {
    setIsLoading(true);

    let result;

    try {
      result = await getVehicleGroupsAPI(initialPagination);
    } catch {
      setIsLoading(false);
      return;
    }

    setVehicleGroups(result);
    setIsLoading(false);
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CALCULATION_TEHNICAL_INSPECTION_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!tehnicalInspectionId) {
        result = await updateTehnicalInspectionAPI(
          tehnicalInspectionId,
          formValues
        );
      } else {
        result = await createTehnicalInspectionAPI(formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  const vehicleGroupItems: DropdownOption[] = vehicleGroups.data.map((i) => {
    return {
      value: i.id.toString(),
      text: i.name,
    };
  });

  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="space-y-3">
            <form noValidate className="text-left">
              <div className="px-5 pb-3 mb-2">
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <Dropdown
                      name="vehicleGroupId"
                      label={t(
                        "admin.calculation.setting.tehnicalinspection.add.vehiclegroup"
                      )}
                      options={vehicleGroupItems}
                      onChange={handleDropdownChange}
                      value={formValues.vehicleGroupId}
                      error={errors.vehicleGroupId}
                      required
                    />
                  </div>
                </div>
                <div className="flex gap-3 mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="number"
                      name="price"
                      label={t(
                        "admin.calculation.setting.tehnicalinspection.add.price"
                      )}
                      placeholder={t(
                        "admin.calculation.setting.tehnicalinspection.add.price.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.price}
                      value={formValues.price}
                      required
                      endIcon=""
                      groupAppend="€"
                    ></TextField>
                  </div>
                </div>
              </div>
              <div className="px-5 pb-5 flex justify-end ">
                <Button
                  variant="primary"
                  size="full"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(TehnicalInspectionEditCard);
